/**
 * @packageDocumentation
 * @module      CorLogin.ts
 * @description Component to display a Login page.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, customElement, html, internalProperty, LitElement, property } from 'lit-element';
import './CorThemeSelector';
import './CorLogoBar';
import LoggedInUser, { LoginResultCode } from "./LoggedInUser";
import { UserLoggedInEvent } from "./UserLoggedInEvent";
import CloudApi from "./CloudApi";
import Fetcher from "./Fetcher";
import ValidationTracker from "./ValidationTracker";
import NewPasswordQualifier from "./NewPasswordQualifier";
import { universalStyles } from './CorMain';
var PasswordResetState;
(function (PasswordResetState) {
    // We are in the process of a normal login, not a password reset.
    PasswordResetState[PasswordResetState["NotResettingPassword"] = 0] = "NotResettingPassword";
    // The user has clicked "Forgot password?" and is in the process of filling out the Reset Password form.
    // If we can't send the email to the user, we remain in this state.
    PasswordResetState[PasswordResetState["RequestingPasswordReset"] = 1] = "RequestingPasswordReset";
    // We have sent an email to the user with a link to reset his password.
    PasswordResetState[PasswordResetState["PasswordResetRequested"] = 2] = "PasswordResetRequested";
    // The user has received the email and we are back here because the user clicked on or pasted the link.
    PasswordResetState[PasswordResetState["BackFromEmail"] = 3] = "BackFromEmail";
})(PasswordResetState || (PasswordResetState = {}));
/** Component to display a Login page. */
let CorLogin = class CorLogin extends LitElement {
    constructor() {
        super();
        this.validationTracker = new ValidationTracker();
        /** The username as entered on the page. */
        this.username = '';
        /** The password as entered on the page. */
        this.password = '';
        /** Confirmation of password. */
        this.confirmPassword = '';
        /**
         *  The result of the user's login attempt.
         *
         *  The initial value is {@linkcode LoginResultCode.NotAttempted} but the value will also revert to that
         *  after the user acknowledges the error modal.
         */
        this.loginResultCode = LoginResultCode.NotAttempted;
        // These variables are set by the username and key paremters on the query string if the user has clicked
        // on or pasted the link in a password-reset email.
        this.passwordResetUsername = null;
        this.passwordResetKey = null;
        this.errorModalRevertFunc = () => {
        };
        this.errorModalLabel = '';
        this.errorModalType = 'error';
        this.errorModalMessage = '';
        this.wantErrorModal = false;
        const urlSearch = new URLSearchParams(window.location.search);
        this.passwordResetUsername = urlSearch.get('username');
        this.passwordResetKey = urlSearch.get('key');
        if (this.passwordResetUsername && this.passwordResetKey) {
            this.passwordResetState = PasswordResetState.BackFromEmail;
            this.username = this.passwordResetUsername || '';
        }
        else {
            this.passwordResetState = PasswordResetState.NotResettingPassword;
        }
        // Remove the query parameters from the URL just to be tidy.
        const uri = window.location.toString();
        if (uri.indexOf("?") > 0) {
            const clean_uri = uri.substring(0, uri.indexOf("?"));
            window.history.replaceState({}, document.title, clean_uri);
        }
    }
    /**
     * LitElement looks for a static property by this name. If it finds one, it applies these styles to the component.
     * See https://lit-element.polymer-project.org/guide/styles#expressions
     */
    static get styles() {
        return css `
          ${universalStyles}
          
          #content-wrapper {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          #content {
            width: 480px;
            margin-top: -40px; // So it's centered on entire screen, accounting for the logo bar
          }

          #title {
            text-align: center;
            margin-bottom: 32px;
          }

          #EmailPrompt {
            margin-bottom: 8px;
          }

          div.flexcolumn {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          div.flexcolumn > * {
            width: fit-content;
            margin-bottom: 6px;
          }
          
          div.instructions {
            margin-bottom: 12px;
          }

          #EmailSent > sh-text {
            margin-bottom: 6px;
          }

          #password, #newPassword, #confirmPassword {
            margin-top: 8px;
          }

          #footer {
            margin-top: 32px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
        `;
    }
    get usernameIsValidEmailAddress() {
        return /^[^ @]+@[^ @]+\.[^ @]+$/.test(this.username);
    }
    /** The prompt to put in the username textbox. */
    get usernameLabel() {
        return this.passwordResetState === PasswordResetState.NotResettingPassword ? 'Username' : 'Email';
    }
    get showUsername() {
        return this.passwordResetState === PasswordResetState.NotResettingPassword ||
            this.passwordResetState === PasswordResetState.RequestingPasswordReset ||
            this.passwordResetState === PasswordResetState.BackFromEmail;
    }
    get pageTitle() {
        switch (this.passwordResetState) {
            default:
            case PasswordResetState.NotResettingPassword:
                return "Corindus GRX-R Systems Dashboard";
            case PasswordResetState.RequestingPasswordReset:
            case PasswordResetState.PasswordResetRequested:
            case PasswordResetState.BackFromEmail:
                return "Reset Password";
        }
    }
    /**
     * Attempt a login with the values provided on-sreen and set {@linkcode CorLogin.loginResultCode}.
     *
     * If login succeeds, fires a {@linkcode UserLoggedInEvent}, which is detected by {@linkcode CorMain},
     * which then displays the main page of the application rather than the login page.
     */
    logIn() {
        LoggedInUser.attemptLogin(this.username, this.password).then((result) => {
            this.loginResultCode = result.code;
            if (this.loginResultCode == LoginResultCode.Success) {
                this.dispatchEvent(new UserLoggedInEvent());
            }
            else if (this.loginResultCode == LoginResultCode.Failed) {
                this.showErrorModal('Login Failed', 'The login failed due to an error on the server. Please contact an administrator.', () => {
                    // this.loginResultCode = LoginResultCode.NotAttempted;
                });
            }
            else if (this.loginResultCode == LoginResultCode.Unauthorized) {
                this.showErrorModal('Login Unauthorized', 'You are not authorized to use this application.', () => {
                    this.password = '';
                    this.loginResultCode = LoginResultCode.NotAttempted;
                });
            }
            else if (this.loginResultCode == LoginResultCode.PasswordExpired) {
                this.showErrorModal('Password Expired', result.message, () => {
                    this.password = '';
                    this.loginResultCode = LoginResultCode.NotAttempted;
                });
            }
            else if (this.loginResultCode == LoginResultCode.AccountLocked) {
                this.showErrorModal('Account Locked', result.message, () => {
                    this.password = '';
                    this.loginResultCode = LoginResultCode.NotAttempted;
                });
            }
            else if (result.message) {
                this.showErrorModal('Login Failed', result.message, // Most likely the failed-login countdown.
                () => {
                    this.loginResultCode = LoginResultCode.WrongPassword;
                }, "alert");
            }
        });
    }
    /**
     * Because LitElement does not have two-way binding, we must copy input values to properties ourselves.
     *
     * Also reinitializes {@linkcode CorLogin.loginResultCode} to {@linkcode LoginResultCode.NotAttempted} so any
     * error messages attached to the input fields go away as soon as the user starts to key in a correction.
     *
     * @param targetProperty The property in this class to which the user's input should be copied.
     */
    inputHelper(targetProperty) {
        const self = this;
        return function (e) {
            if (e.currentTarget instanceof Element) {
                self.loginResultCode = LoginResultCode.NotAttempted;
                // @ts-ignore implicit type of any
                self[targetProperty] = e.currentTarget.getAttribute('value') || '';
            }
        };
    }
    showErrorModal(label, message, revert, type) {
        this.errorModalLabel = label;
        this.errorModalMessage = message;
        this.errorModalRevertFunc = revert;
        this.errorModalType = type || 'error';
        this.wantErrorModal = true;
    }
    /** Closes the error modal. */
    errorModalClosed() {
        this.errorModalMessage = ''; // Just to be safe.
        this.wantErrorModal = false;
        this.errorModalRevertFunc();
    }
    initiatePasswordReset() {
        this.passwordResetState = PasswordResetState.RequestingPasswordReset;
    }
    returnToLogin() {
        this.passwordResetState = PasswordResetState.NotResettingPassword;
    }
    requestPasswordReset() {
        const api = new CloudApi(new Fetcher());
        api.requestPasswordReset(this.username).then(() => {
            this.passwordResetState = PasswordResetState.PasswordResetRequested;
        }, (error) => {
            this.showErrorModal('Password Reset Failed', 'Password reset failed due to an error on the server: ' + error, this.initiatePasswordReset);
        });
    }
    processPasswordReset() {
        const api = new CloudApi(new Fetcher());
        api.processPasswordReset(this.username, this.passwordResetKey || '', this.password).then(() => {
            this.logIn();
        }, (error) => {
            this.showErrorModal('Password Reset Failed', 'Password reset failed due to an error on the server: ' + error, () => { } // Stay here.
            );
        });
    }
    isValidNewPassword() {
        return NewPasswordQualifier.qualifies(this.password, this.username);
    }
    isValidConfirmPassword() {
        return this.confirmPassword.length === 0 ||
            this.confirmPassword === this.password;
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        var _a, _b;
        return html `
            <sh-card>
                <cor-logo-bar></cor-logo-bar>
                <div id="content-wrapper">
                    <div id="content">
                        <sh-text id="title" size="super-header">${this.pageTitle}</sh-text>
                        ${this.passwordResetState === PasswordResetState.RequestingPasswordReset ?
            html `
                                    <sh-text id="EmailPrompt" size="body-2" color="secondary">Enter the email address
                                        you're using for your account.
                                    </sh-text>` :
            html ``}
                        ${this.passwordResetState === PasswordResetState.PasswordResetRequested ?
            html `
                                    <div class="flexcolumn instructions">
                                        <sh-text size="body-1" color="secondary">
                                            An email has been sent to ${this.username}
                                        </sh-text>
                                        <sh-text size="body-1" color="secondary">
                                            with instructions for resetting your password.
                                        </sh-text>
                                        <sh-text size="body-1" color="secondary">
                                            If it does not appear in your Inbox soon,
                                        </sh-text>
                                        <sh-text size="body-1" color="secondary">
                                            be sure to check your Junk Email folder.
                                        </sh-text>
                                    </div>` : html ``}
                        ${this.passwordResetState === PasswordResetState.BackFromEmail ?
            html `
                                    <div class="instructions">
                                        <sh-text size="body-1" color="secondary">Please make sure to use 8 or more
                                            characters with at least one letter, one
                                        </sh-text>
                                        <sh-text size="body-1" color="secondary">number, and one special character.
                                            The password must not include the username.
                                        </sh-text>
                                    </div>` : html ``}
                        ${this.showUsername ?
            html `
                                    <sh-input-text id="username" label='${this.usernameLabel}' value="${this.username}"
                                                   autofocus
                                                   mandatory
                                                   ?readonly=${this.passwordResetState === PasswordResetState.BackFromEmail}
                                                   @input=${this.inputHelper('username')}
                                                   ?error="${this.loginResultCode === LoginResultCode.InvalidUsername}"
                                                   error-message="That's not a valid username. Please try again or contact admin.">
                                    </sh-input-text>` : html ``}
                        ${this.passwordResetState === PasswordResetState.NotResettingPassword ?
            html `
                                    <sh-input-text id="password" label='Password' value="${this.password}"
                                                   mandatory
                                                   @input=${this.inputHelper('password')}
                                                   ?error="${this.loginResultCode === LoginResultCode.WrongPassword}"
                                                   error-message="That's an incorrect password. Try again."
                                                   type="password">
                                    </sh-input-text>` : html ``}
                        ${this.passwordResetState === PasswordResetState.BackFromEmail ?
            html `
                                    <sh-input-text id="newPassword" label='New Password' value="${this.password}"
                                                   mandatory
                                                   @input=${this.inputHelper('password')}
                                                   ?success=${((_a = this.password) === null || _a === void 0 ? void 0 : _a.length) > 0 &&
                !this.validationTracker.error('newPassword', this.isValidNewPassword.bind(this))}
                                                   ?error=${this.validationTracker.error('newPassword', this.isValidNewPassword.bind(this))}
                                                   error-message="Password does not meet complexity requirements."
                                                   type="password">
                                    </sh-input-text>
                                    <sh-input-text id="confirmPassword" label='Confirm New Password'
                                                   value="${this.confirmPassword}"
                                                   mandatory
                                                   @input=${this.inputHelper('confirmPassword')}
                                                   ?success=${((_b = this.confirmPassword) === null || _b === void 0 ? void 0 : _b.length) > 0 &&
                !this.validationTracker.error('confirmPassword', this.isValidConfirmPassword.bind(this))}
                                                   ?error=${this.validationTracker.error('confirmPassword', this.isValidConfirmPassword.bind(this))}
                                                   error-message="Confirmation password must match the new password."
                                                   type="password">
                                    </sh-input-text>` : html ``}
                        <div id="footer">
                            ${this.passwordResetState === PasswordResetState.NotResettingPassword ?
            html `
                                        <sh-text id="ForgotPassword" href="#" size='body-2' color="secondary"
                                                 @click="${this.initiatePasswordReset}">Forgot password?
                                        </sh-text>
                                        <sh-button id="login" label="Log In"
                                                   ?disabled="${this.username.trim().length === 0 || this.password.trim().length === 0}"
                                                   @click="${this.logIn}"></sh-button>` :
            html ``}
                            ${this.passwordResetState === PasswordResetState.RequestingPasswordReset ?
            html `
                                        <sh-button label="Cancel" color="secondary"
                                                   @click="${this.returnToLogin}">
                                        </sh-button>
                                        <sh-button label="Continue"
                                                   ?disabled="${!this.usernameIsValidEmailAddress}"
                                                   @click="${this.requestPasswordReset}">
                                        </sh-button>` : html ``}
                            ${this.passwordResetState === PasswordResetState.PasswordResetRequested ?
            html `
                                        <div class="flexcolumn">
                                            <sh-button label="Resend"
                                                       color="secondary"
                                            "
                                                @click="${this.requestPasswordReset}">
                                            </sh-button>
                                            <sh-text href="#" size='body-2' color="secondary"
                                                     @click="${this.returnToLogin}">Back to Login
                                            </sh-text>
                                        </div>` : html ``}
                            ${this.passwordResetState === PasswordResetState.BackFromEmail ?
            html `
                                        <sh-button label="Cancel" color="secondary"
                                                   @click="${this.returnToLogin}">
                                        </sh-button>
                                        <sh-button label="Reset Password" size="l"
                                                   ?disabled=${this.validationTracker.anyInvalid ||
                !this.password ||
                !this.confirmPassword}
                                                   @click="${this.processPasswordReset}"></sh-button>` : html ``}
                        </div>
                    </div>
                </div>
                <sh-modal id="error-modal" label=${this.errorModalLabel}
                          modal-height='250px' modal-width='400px'
                          sticky
                          type=${this.errorModalType}
                          ?visible="${this.wantErrorModal}"
                          @modal-closed="${() => this.errorModalClosed()}"
                >
                    <sh-text>${this.errorModalMessage}</sh-text>
                    <sh-button slot="footer" label="Close"
                               @click="${() => this.errorModalClosed()}"></sh-button>
                </sh-modal>
            </sh-card>
        `;
    }
};
__decorate([
    internalProperty()
], CorLogin.prototype, "passwordResetState", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "username", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "usernameIsValidEmailAddress", null);
__decorate([
    internalProperty()
], CorLogin.prototype, "password", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "confirmPassword", void 0);
__decorate([
    property({ type: LoginResultCode })
], CorLogin.prototype, "loginResultCode", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "showUsername", null);
__decorate([
    internalProperty()
], CorLogin.prototype, "pageTitle", null);
__decorate([
    internalProperty()
], CorLogin.prototype, "errorModalLabel", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "errorModalType", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "errorModalMessage", void 0);
__decorate([
    internalProperty()
], CorLogin.prototype, "wantErrorModal", void 0);
CorLogin = __decorate([
    customElement('cor-login')
], CorLogin);
export { CorLogin };
//# sourceMappingURL=CorLogin.js.map